import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Users,
  UserPlus,
  UserRoundCheck,
  UserRoundX,
  Search,
  Filter,
  ArrowUpDown,
  ChevronDown,
  Download,
  X,
  Loader2,
  Trash2,
  Pencil,
} from "lucide-react";
import MainLayout from "../sidebar/Sidebar.js";
import { Button } from "../components/ui/button";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../api/api.js";
import { useAuth } from "../context/AuthContext.js";

// Lead Modal component - defined outside to prevent re-creation on parent re-render
const LeadModal = ({ isOpen, onClose, title, onSubmit, isLoading: isSubmitting, formData, setFormData, channels, statuses }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <form onSubmit={onSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter phone number"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Channel
              </label>
              <select
                value={formData.channel || ""}
                onChange={(e) => setFormData((p) => ({ ...p, channel: e.target.value || null }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select channel</option>
                {channels.map((ch) => (
                  <option key={ch} value={ch}>
                    {ch}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assigned To
              </label>
              <input
                type="text"
                value={formData.assigned_to}
                onChange={(e) => setFormData((p) => ({ ...p, assigned_to: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter assignee name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData((p) => ({ ...p, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {statuses.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : title.includes("Edit") ? (
                "Save Changes"
              ) : (
                "Add Lead"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Custom hook for debounced search
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const CrmNewDesktop = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [location, setLocation] = useState("CrmNew");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [hoveredRowIndex, setHoveredRowIndex] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [editingLead, setEditingLead] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    channel: null,
    assigned_to: "",
    status: "New",
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Fetch leads from API
  const {
    data: leads = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["leads", user?.uid, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      const url = `/leads/${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await api.get(url);
      return response.data;
    },
    staleTime: 1 * 60 * 1000,
    enabled: !!user,
  });

  // Fetch channels and statuses
  const { data: channels = [] } = useQuery({
    queryKey: ["leadChannels"],
    queryFn: async () => {
      const response = await api.get("/leads/channels/list");
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
  });

  const { data: statuses = [] } = useQuery({
    queryKey: ["leadStatuses"],
    queryFn: async () => {
      const response = await api.get("/leads/statuses/list");
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
  });

  // Create lead mutation
  const createLeadMutation = useMutation({
    mutationFn: async (leadData) => {
      const response = await api.post("/leads/", leadData);
      return response.data;
    },
    onSuccess: (newLead) => {
      queryClient.setQueryData(["leads", user?.uid, statusFilter], (old) => {
        return [newLead, ...(old || [])];
      });
      queryClient.invalidateQueries(["leads"]);
      setShowCreateModal(false);
      resetForm();
    },
  });

  // Update lead mutation
  const updateLeadMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/leads/${id}`, data);
      return response.data;
    },
    onSuccess: (updatedLead) => {
      queryClient.setQueryData(["leads", user?.uid, statusFilter], (old) => {
        return (old || []).map((lead) =>
          lead.id === updatedLead.id ? updatedLead : lead
        );
      });
      queryClient.invalidateQueries(["leads"]);
      setShowEditModal(false);
      setEditingLead(null);
      resetForm();
    },
  });

  // Delete lead mutation
  const deleteLeadMutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/leads/${id}`);
      return id;
    },
    onSuccess: (deletedId) => {
      queryClient.setQueryData(["leads", user?.uid, statusFilter], (old) => {
        return (old || []).filter((lead) => lead.id !== deletedId);
      });
      queryClient.invalidateQueries(["leads"]);
      setShowDeleteConfirm(false);
      setLeadToDelete(null);
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      channel: null,
      assigned_to: "",
      status: "New",
    });
  };

  const handleOpenEditModal = useCallback((lead) => {
    setEditingLead(lead);
    setFormData({
      name: lead.name || "",
      email: lead.email || "",
      phone: lead.phone || "",
      channel: lead.channel || null,
      assigned_to: lead.assigned_to || "",
      status: lead.status || "New",
    });
    setShowEditModal(true);
  }, []);

  const handleOpenDeleteConfirm = useCallback((lead) => {
    setLeadToDelete(lead);
    setShowDeleteConfirm(true);
  }, []);

  const handleToggleSelect = (id) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleToggleSelectAll = () => {
    if (selectedItems.size === filteredLeads.length && filteredLeads.length > 0) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredLeads.map((lead) => lead.id)));
    }
  };

  // Filter leads based on search
  const filteredLeads = useMemo(() => {
    if (!debouncedSearchTerm) return leads;
    const term = debouncedSearchTerm.toLowerCase();
    return leads.filter(
      (lead) =>
        lead.name?.toLowerCase().includes(term) ||
        lead.email?.toLowerCase().includes(term) ||
        lead.phone?.includes(term)
    );
  }, [leads, debouncedSearchTerm]);

  // Calculate metrics
  const metrics = useMemo(() => {
    const total = leads.length;
    const newCount = leads.filter((l) => l.status === "New").length;
    const qualified = leads.filter((l) => l.status === "Qualified").length;
    const lost = leads.filter((l) => l.status === "Lost").length;
    return { total, newCount, qualified, lost };
  }, [leads]);

  const getStatusBadge = (status) => {
    const statusStyles = {
      Contacted: "bg-blue-100 text-blue-800 border border-blue-200",
      New: "bg-gray-100 text-gray-800 border border-gray-200",
      Qualified: "bg-green-100 text-green-800 border border-green-200",
      Lost: "bg-purple-100 text-purple-800 border border-purple-200",
    };

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[status] || statusStyles.New}`}
      >
        {status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  const handleSubmitCreate = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    createLeadMutation.mutate({
      name: formData.name.trim(),
      email: formData.email.trim() || null,
      phone: formData.phone.trim() || null,
      channel: formData.channel || null,
      assigned_to: formData.assigned_to.trim() || null,
      status: formData.status || "New",
    });
  };

  const handleSubmitEdit = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !editingLead) return;
    updateLeadMutation.mutate({
      id: editingLead.id,
      data: {
        name: formData.name.trim(),
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        channel: formData.channel || null,
        assigned_to: formData.assigned_to.trim() || null,
        status: formData.status || "New",
      },
    });
  };

  const handleConfirmDelete = () => {
    if (leadToDelete) {
      deleteLeadMutation.mutate(leadToDelete.id);
    }
  };

  // Delete confirmation modal
  const DeleteConfirmModal = () => {
    if (!showDeleteConfirm) return null;
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Delete Lead</h2>
          <p className="text-gray-600 mb-6">
            Are you sure you want to delete "{leadToDelete?.name}"? This action cannot be undone.
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                setShowDeleteConfirm(false);
                setLeadToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              onClick={handleConfirmDelete}
              disabled={deleteLeadMutation.isPending}
            >
              {deleteLeadMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Delete"
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <MainLayout location={location} setLocation={setLocation}>
      <div className="py-6 px-5" style={{ backgroundColor: 'rgba(0, 109, 226, 0)' }}>
        {/* Metrics Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {/* Total Leads */}
          <div className="bg-white p-4 rounded-2xl border border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Users className="w-5 h-5 text-gray-600" />
              </div>
              <span className="text-base" style={{ color: '#424242' }}>Total Leads</span>
            </div>
            <div className="text-2xl font-light" style={{ color: '#424242' }}>{metrics.total}</div>
            <div className="text-sm text-gray-500">All leads</div>
          </div>

          {/* New Leads */}
          <div className="bg-white p-4 rounded-2xl border border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <UserPlus className="w-5 h-5 text-gray-600" />
              </div>
              <span className="text-base" style={{ color: '#424242' }}>New Leads</span>
            </div>
            <div className="text-2xl font-light" style={{ color: '#424242' }}>{metrics.newCount}</div>
            <div className="text-sm text-gray-500">Awaiting contact</div>
          </div>

          {/* Qualified Leads */}
          <div className="bg-white p-4 rounded-2xl border border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <UserRoundCheck className="w-5 h-5 text-gray-600" />
              </div>
              <span className="text-base" style={{ color: '#424242' }}>Qualified Leads</span>
            </div>
            <div className="text-2xl font-light" style={{ color: '#424242' }}>{metrics.qualified}</div>
            <div className="text-sm text-gray-500">{metrics.total > 0 ? Math.round((metrics.qualified / metrics.total) * 100) : 0}% of total</div>
          </div>

          {/* Lost Leads */}
          <div className="bg-white p-4 rounded-2xl border border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <UserRoundX className="w-5 h-5 text-gray-600" />
              </div>
              <span className="text-base" style={{ color: '#424242' }}>Lost Leads</span>
            </div>
            <div className="text-2xl font-light" style={{ color: '#424242' }}>{metrics.lost}</div>
            <div className="text-sm text-gray-500">{metrics.total > 0 ? Math.round((metrics.lost / metrics.total) * 100) : 0}% of total</div>
          </div>
        </div>

        {/* Controls Row */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: "#727272" }} />
              <input
                type="text"
                placeholder="Search here..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 rounded-lg bg-white text-sm shadow-sm focus:outline-none focus:ring-0"
                style={{ border: "1px solid #e8e8e8", color: "#727272", padding: "7px 11px", paddingLeft: "40px" }}
              />
            </div>

            {/* Sort + Filter */}
            <div className="flex gap-2">
              <Button variant="customStandard" size="customStandard">
                <ArrowUpDown className="w-4 h-4" />
                Sort
                <ChevronDown className="w-4 h-4" />
              </Button>
              <div className="relative">
                <Button
                  variant="customStandard"
                  size="customStandard"
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                >
                  <Filter className="w-4 h-4" />
                  Filter{statusFilter ? `: ${statusFilter}` : ""}
                  <ChevronDown className="w-4 h-4" />
                </Button>
                {showFilterDropdown && (
                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[150px]">
                    <button
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50"
                      onClick={() => { setStatusFilter(null); setShowFilterDropdown(false); }}
                    >
                      All Statuses
                    </button>
                    {statuses.map((st) => (
                      <button
                        key={st}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50"
                        onClick={() => { setStatusFilter(st); setShowFilterDropdown(false); }}
                      >
                        {st}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Side Actions */}
          <div className="flex gap-2">
            <Button
              variant="customStandard"
              size="customStandard"
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
            >
              <UserPlus className="w-4 h-4" />
              Add Customer
            </Button>
            <Button variant="customStandard" size="customStandard">
              <Download className="w-4 h-4" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12 text-red-600">
            Failed to load leads. Please try again.
          </div>
        )}

        {/* Table Section */}
        {!isLoading && !error && (
          <div className="overflow-x-auto rounded-2xl" style={{ boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.02), 0 -2px 12px -4px rgba(0, 0, 0, 0.02), 0 4px 16px -6px rgba(0, 0, 0, 0.06), 0 8px 20px -10px rgba(0, 0, 0, 0.04)', border: '1px solid rgba(0, 0, 0, 0.12)' }}>
            <table className="w-full" style={{ backgroundColor: 'rgba(0, 0, 0, 0.15)' }}>
              <thead className="sticky top-0 z-10" style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
                <tr>
                  <th className="px-4 py-4 w-12">
                    <button
                      onClick={handleToggleSelectAll}
                      className="w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors p-0.5"
                      style={{
                        borderColor: selectedItems.size === filteredLeads.length && filteredLeads.length > 0 ? '#006de2' : '#d1d5db',
                        backgroundColor: 'transparent',
                      }}
                    >
                      {selectedItems.size === filteredLeads.length && filteredLeads.length > 0 && (
                        <div
                          className="w-full h-full rounded"
                          style={{ backgroundColor: '#006de2' }}
                        />
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left font-medium tracking-wider" style={{ color: '#727272', fontSize: '14px' }}>
                    Lead
                  </th>
                  <th className="px-6 py-4 text-left font-medium tracking-wider" style={{ color: '#727272', fontSize: '14px' }}>
                    Phone
                  </th>
                  <th className="px-6 py-4 text-left font-medium tracking-wider" style={{ color: '#727272', fontSize: '14px' }}>
                    Channel
                  </th>
                  <th className="px-6 py-4 text-left font-medium tracking-wider" style={{ color: '#727272', fontSize: '14px' }}>
                    Assigned To
                  </th>
                  <th className="px-6 py-4 text-left font-medium tracking-wider" style={{ color: '#727272', fontSize: '14px' }}>
                    Status
                  </th>
                  <th className="px-6 py-4 text-left font-medium tracking-wider" style={{ color: '#727272', fontSize: '14px' }}>
                    Created
                  </th>
                  <th className="px-4 py-4 w-20"></th>
                </tr>
                <tr>
                  <th colSpan="8" className="p-0">
                    <div className="mx-4" style={{ borderBottom: '1px solid #e8e8e8' }} />
                  </th>
                </tr>
              </thead>
              <tbody style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
                {filteredLeads.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                      {searchTerm ? "No leads match your search" : "No leads yet. Click 'Add Customer' to create one."}
                    </td>
                  </tr>
                ) : (
                  filteredLeads.map((lead, index) => (
                    <React.Fragment key={lead.id}>
                      <tr
                        className="cursor-pointer"
                        onMouseEnter={() => setHoveredRowIndex(index)}
                        onMouseLeave={() => setHoveredRowIndex(null)}
                      >
                        <td className="py-4 pl-4 pr-0" style={{ position: 'relative' }}>
                          <div
                            className="row-highlight"
                            style={{
                              position: 'absolute',
                              top: 0,
                              bottom: 0,
                              left: '0.5rem',
                              right: 0,
                              pointerEvents: 'none',
                              zIndex: 0,
                              borderRadius: '8px 0 0 8px',
                              backgroundColor: (selectedItems.has(lead.id) || hoveredRowIndex === index) ? '#edfaff' : undefined,
                              transition: 'background-color 0.15s ease',
                            }}
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleSelect(lead.id);
                            }}
                            className="w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors p-0.5"
                            style={{
                              borderColor: selectedItems.has(lead.id) ? '#006de2' : '#d1d5db',
                              backgroundColor: 'transparent',
                              position: 'relative',
                              zIndex: 1,
                            }}
                          >
                            {selectedItems.has(lead.id) && (
                              <div
                                className="w-full h-full rounded"
                                style={{ backgroundColor: '#006de2' }}
                              />
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4" style={{ backgroundColor: (selectedItems.has(lead.id) || hoveredRowIndex === index) ? '#edfaff' : undefined, transition: 'background-color 0.15s ease' }}>
                          <div>
                            <div className="text-sm" style={{ color: '#424242' }}>{lead.name}</div>
                            <div className="text-sm" style={{ color: '#727272' }}>
                              {lead.email || "-"}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm" style={{ color: '#424242', backgroundColor: (selectedItems.has(lead.id) || hoveredRowIndex === index) ? '#edfaff' : undefined, transition: 'background-color 0.15s ease' }}>
                          {lead.phone || "-"}
                        </td>
                        <td className="px-6 py-4 text-sm" style={{ color: '#424242', backgroundColor: (selectedItems.has(lead.id) || hoveredRowIndex === index) ? '#edfaff' : undefined, transition: 'background-color 0.15s ease' }}>
                          {lead.channel || "-"}
                        </td>
                        <td className="px-6 py-4 text-sm" style={{ color: '#424242', backgroundColor: (selectedItems.has(lead.id) || hoveredRowIndex === index) ? '#edfaff' : undefined, transition: 'background-color 0.15s ease' }}>
                          {lead.assigned_to || "Unassigned"}
                        </td>
                        <td className="px-6 py-4" style={{ backgroundColor: (selectedItems.has(lead.id) || hoveredRowIndex === index) ? '#edfaff' : undefined, transition: 'background-color 0.15s ease' }}>{getStatusBadge(lead.status)}</td>
                        <td className="px-6 py-4 text-sm" style={{ color: '#424242', backgroundColor: (selectedItems.has(lead.id) || hoveredRowIndex === index) ? '#edfaff' : undefined, transition: 'background-color 0.15s ease' }}>
                          {formatDate(lead.created_at)}
                        </td>
                        <td className="py-4 pr-4 pl-2" style={{ position: 'relative' }}>
                          <div
                            className="row-highlight-end"
                            style={{
                              position: 'absolute',
                              top: 0,
                              bottom: 0,
                              left: 0,
                              right: '0.5rem',
                              pointerEvents: 'none',
                              zIndex: 0,
                              borderRadius: '0 8px 8px 0',
                              backgroundColor: (selectedItems.has(lead.id) || hoveredRowIndex === index) ? '#edfaff' : undefined,
                              transition: 'background-color 0.15s ease',
                            }}
                          />
                          <div className="flex gap-1" style={{ position: 'relative', zIndex: 1 }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEditModal(lead);
                              }}
                              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                              <Pencil className="w-4 h-4 text-gray-500" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDeleteConfirm(lead);
                              }}
                              className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4 text-gray-500 hover:text-red-600" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {index < filteredLeads.length - 1 && (
                        <tr>
                          <td colSpan="8" className="p-0">
                            <div className="mx-4" style={{ borderBottom: '1px solid #e8e8e8' }} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      <LeadModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        title="Add New Lead"
        onSubmit={handleSubmitCreate}
        isLoading={createLeadMutation.isPending}
        formData={formData}
        setFormData={setFormData}
        channels={channels}
        statuses={statuses}
      />

      <LeadModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingLead(null);
          resetForm();
        }}
        title="Edit Lead"
        onSubmit={handleSubmitEdit}
        isLoading={updateLeadMutation.isPending}
        formData={formData}
        setFormData={setFormData}
        channels={channels}
        statuses={statuses}
      />

      <DeleteConfirmModal />
    </MainLayout>
  );
};

export default CrmNewDesktop;
